import React, { FormHTMLAttributes, useState } from 'react'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import FormControl from '@/components/base/FormControl'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { convertJsontoFormData, createInitialValues, createYupSchema } from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import Button from './Button'
import Spinner from './Spinner'
import TypographyMask from './TypographyMask'
import { cn } from '@/lib/utils'
import { WPCF7Form } from '@/types'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: string
    submit: string
    fields: WPCF7Form['form']
  }>

const Form = ({ form_id, fields = [], submit, className, ...props }: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, 'en'),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const result = sendContactForm({
        ...values
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            resetForm()
            setStatusMessage('&nbsp;')
            setStatus('')

            setSubmitting(false)
          }, 3000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className={cn(['relative grid grid-cols-2 gap-spacing-xxs', className])}
      {...props}
    >
      {fields?.map((field, index) => {
        return (
          <FormControl
            key={index}
            id={`input_${field.id}`}
            name={`input_${field.id}`}
            type={field.type}
            label={field.type === 'consent' ? field.checkboxLabel : field.label}
            required={field.isRequired}
            choices={field.choices}
            placeholder={`${field.placeholder}${field.isRequired ? ' *' : ''}`}
            checked={formik.values[`input_${field.id}`] || false}
            value={formik.values[`input_${field.id}`]}
            disabled={formik.isSubmitting}
            onChange={(e) => {
              formik.handleChange(e)
              if (field.type === 'consent') {
                formik.setFieldValue(`input_${field.id}_1`, e.target.checked ? '1' : '0', false)
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined
            }
            className={cn([
              'col-span-2',
              {
                'md:col-span-1': field.cssClass === 'half',
                'mt-spacing-xs': field.type === 'consent'
              }
            ])}
          />
        )
      })}

      <div className="col-span-2 mt-spacing-xs flex items-center gap-spacing-xs">
        <Button
          type="submit"
          className={cn([
            'w-full justify-between rounded-radius-minimal py-spacing-xs',
            {
              'pointer-events-none opacity-40': formik.isSubmitting
            }
          ])}
          endIcon={
            <SVGIcon
              symbol="arrow-right"
              className={cn('h-[0.8rem] w-[4rem] fill-surface-primay-invert-dark', {
                'opacity-0': formik.isSubmitting
              })}
            />
          }
          disabled={formik.isSubmitting}
        >
          <TypographyMask>
            <Typography variant="regular" className="text-text-primary-invert-dark">
              {submit}
            </Typography>
          </TypographyMask>
          {formik.isSubmitting ? (
            <div className="spinner !absolute right-spacing-md top-1/2 -translate-y-[0.3rem]">
              <Spinner variant="small" />
            </div>
          ) : null}
        </Button>
      </div>

      {statusMessage !== '' ? (
        <div
          className={cn([
            'status-message',
            'col-span-2 mt-spacing-xxs flex font-roboto text-small',
            {
              'text-text-primary-dark': formik.status !== 'error',
              'text-error': formik.status === 'error'
            }
          ])}
        >
          {parse(statusMessage)}
        </div>
      ) : null}
    </form>
  )
}

export default Form
